// Dashboard (§6 /dashboard) — redesigned to a fixed top-to-bottom worklist (owner 2026-09-19).
// Exactly six sections, in this order and nothing else:
//   1. Critical metrics — the KPI tiles (kpisView).
//   2. Unallocated bank imports — the cross-account bank inbox worklist (bankInboxNode).
//   3. Bank Accounts — FULL WIDTH: each row opens that account, balances + a Total Cash row.
//   4. Profit & Loss — FULL WIDTH: this month + year to date (income / expenses / net).
//   5. Drafts to send or confirm — draft documents (and issued-but-unsent) needing action; each
//      row opens the document editor. Replaces the old "Client activity" feed.
//   6. Recent Activity — LAST: the latest posted movements; each row opens its source.
// Removed in this redesign: the scan-a-document chooser, the client-activity feed, the receivables
// aging + sales-by-month charts, and the "Needs your attention" prompt.
// Data: /dashboard summary (bank accounts, P&L, recent activity, KPIs, receivables) +
// /companies/{id}/documents (the drafts list) + the bank inbox's own endpoints.
import { html, signal, navigate } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { dashGo, takePendingDashScroll } from '../shared/dashnav.js';
import { mountFloatRoot, closeFloatRoot } from '../shared/floatroot.js';
import { dropdownKeydown, highlightFirst } from '../shared/dropnav.js';
import { tenantShell } from './tshell.js';
import { fmtR, fmtRS, fmtR0, fmtMoney, baseCurrencySymbol, formatDate, formatGlCode, scoreGL, confPct, controlTag, totalTone, suggestionFor, bankFigures, docCurrency, fmtIn, baseCurrency, setBaseCurrencySymbol, baseCurrencyCode, setBaseCurrencyCode } from './bank_helpers.js';
import { companyCurrencies, needsCurrencyLookup } from '../shared/currencies.js';
import { DOC_TYPES, documentTypeLabel, statusClass, statusLabel, statusHelp, isDraftDocNo } from './documents.js';
import { movedOutText } from './coop_helpers.js';
import { t } from '../shared/i18n.js';

// type_code → route key (INV → invoices …), for opening a document's editor from the drafts list
const TYPE_KEY = Object.fromEntries(Object.entries(DOC_TYPES).map(([k, cfg]) => [cfg.type, k]));

// Collapsible blocks (iT dash-coll): header toggles, state remembered per browser, count stays
// visible while folded. Pure class-toggle on the live card — no re-render.
const COLL_KEY = 'sleek-dash-coll';
function collOn(key){
  try { return !!JSON.parse(localStorage.getItem(COLL_KEY) || '{}')[key]; } catch { return false; }
}
function toggleColl(e){
  const head = e.currentTarget;
  const card = head.closest('.dash-coll');
  if(!card) return;
  const on = card.classList.toggle('is-collapsed');
  const ico = head.querySelector('.coll-ico'); if(ico) ico.textContent = on ? '▸' : '▾';
  head.setAttribute('aria-expanded', on ? 'false' : 'true');
  try { const st = JSON.parse(localStorage.getItem(COLL_KEY) || '{}');
    st[card.getAttribute('data-coll')] = on;
    localStorage.setItem(COLL_KEY, JSON.stringify(st)); } catch { /* private mode */ }
}
const collKey = e => { if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); toggleColl(e); } };

// Wrap any dashboard block in the collapsible card shell (iT dash-coll) so EVERY section folds
// (owner 2026-09-06: "everything in the dashboard must be collapsible"). `title`/`hint`/`countText`
// are strings, `body` is html/nodes. collOn(key) is read at call time as a plain value — safe inside
// a re-rendered reactive view: toggleColl class-toggles the live node, and a later re-render simply
// re-reads the persisted state. No nested ${()=>…} inside (would append duplicates on re-render).
function collCard(key, title, hint, countText, body, extraCls){
  const folded = collOn(key);
  return html`<div class=${'card dash-coll' + (folded ? ' is-collapsed' : '') + (extraCls ? ' ' + extraCls : '')} data-coll=${key}>
    <h3 class="coll-toggle" role="button" tabindex="0" aria-expanded=${folded ? 'false' : 'true'}
      title=${t('dashboard.coll_hint', 'Collapse or expand this block')} @click=${toggleColl} @keydown=${collKey}>
      <span class="coll-ico">${folded ? icon('chevron-right', {size: 12}) : icon('chevron-down', {size: 12})}</span>${title}${hint ? html` <small>${hint}</small>` : ''}${countText ? html`<span class="coll-n">${countText}</span>` : ''}</h3>
    <div class="coll-body">${body}</div>
  </div>`;
}

// ── Bank inbox / "Needs attention" (seamless bank flow — Andre huddle 2026-09-16) ────────────
// The retired import-review step's replacement. Imported lines now flow straight into each account's
// register (matched → auto-post; the rest stay UNPOSTED), and this card surfaces EVERY unallocated
// line across ALL the company's bank accounts, grouped by account, so a user quick-allocates them
// (≤2 clicks) WITHOUT opening each cashbook. A scoreGL chip allocates+posts in ONE click; a GL
// <select> is the two-click path. Allocating posts the line immediately (post:true) and drops it
// from the worklist. `count` is the account's TRUE unallocated total (its `lines` are capped at
// 200/account server-side), so a group can advertise "+ N more — open the account".
//
// Built as a STABLE node (the cactNode/scanChooserNode pattern) with two TOP-LEVEL reactive slots
// (header count + body). Every view reads its signals at the top and returns plain values — no
// nested ${()=>…} (the tina4 append gotcha). The per-line <select> lives inside the reactive body
// (there is no stable per-line host); that is safe here because it carries no bound signal state —
// choosing IS the terminal action (allocate+post+remove), so a fresh placeholder on each re-render
// is exactly what we want. Exported so it is unit-testable without mounting the whole tenant shell.
export function bankInboxSection(companyId, opts){
  const co = p => '/co/' + companyId + p;
  const nav = (opts && opts.go) ? opts.go : navigate;   // dashboard passes its scroll-aware go()
  const data = signal(null), ref = signal(null), loading = signal(true), err = signal('');
  const busyId = signal(0), rowErr = signal(''), okMsg = signal('');
  const selAcct = signal(null);          // account whose lines are shown (Bank Accounts strip parity)
  const okId = signal(0);                // line id currently flashing green + ✓ before it drops
  const inboxDelAsk = signal(0);         // line id whose ✕ delete is armed (inline Delete? Yes/No)
  const locate = signal(null);           // 🔎 "see this line on the statement" modal state (register parity)
  let locateSeq = 0;                     // invalidates an in-flight page load when the user closes/re-opens
  let okTimer = null, rowOkTimer = null, okAcct = 0;
  const OK_HOLD_MS = 900;                // v2 bank-import success hold (index.twig ~800ms) before removal
  // shared floating-popover layer + placement (cashbook pattern) — drives the searchable GL picker
  const floatRoot = mountFloatRoot();
  const closeFloat = closeFloatRoot;
  function positionNear(el, anchor, width){
    const r = anchor.getBoundingClientRect();
    el.style.position = 'fixed';
    el.style.left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) + 'px';
    el.style.zIndex = 300;
    floatRoot.appendChild(el);
    const h = el.offsetHeight, below = window.innerHeight - r.bottom - 8;
    el.style.top = (below >= h ? r.bottom + 4 : Math.max(8, r.top - h - 4)) + 'px';
  }

  // the cashbook's GL candidate pool, verbatim (bank + control + the unallocated placeholder out)
  const glPool = () => (((ref.value || {}).accounts) || []).filter(a =>
    a.is_postable && !a.is_bank && !a.control_type && a.code !== '0000.000' && a.code !== '0000');
  const df = () => (ref.value || {}).date_format || 'dd/MM/yyyy';

  // Allocation destinations for the per-line dest-tab row — General plus a tab for each control
  // account the company actually has, read from the SAME /bank-reference bundle the cashbook uses
  // (cashbook.js renderDests): a Debtor/Creditor control account for D/C, VAT registration + a VAT
  // control account for V. Mirrors v2's dashboard triage-dest-row (index.twig setTriageDest). The
  // row is only shown when more than one dest exists (a lone "General" is noise — v2's hasMultipleDests).
  const destList = () => {
    const r = ref.value || {};
    const d = [['G', t('bank_import.general', 'General')]];
    if(r.ar_control_account_id) d.push(['D', t('dashboard.debtors', 'Debtors')]);
    if(r.ap_control_account_id) d.push(['C', t('dashboard.creditors', 'Creditors')]);
    if(r.vat_registered && r.vat_control_account_id) d.push(['V', t('common.vat', 'VAT')]);
    return d;
  };

  async function load(){
    loading.value = true; err.value = '';
    try {
      const [na, rf] = await Promise.all([
        api.get('/companies/' + companyId + '/bank/needs-attention'),
        api.get('/companies/' + companyId + '/bank-reference'),   // same bundle the cashbook loads
      ]);
      data.value = na && na.groups ? na : { groups: [], total: (na && na.total) || 0 };
      ref.value = rf || {};
      // default to the first account (one account shown at a time, like the cashbook Bank Accounts
      // strip); keep the user's current pick if it is still present after a reload.
      const gs = (data.value.groups) || [];
      if(!gs.some(g => g.account_id === selAcct.value)) selAcct.value = gs.length ? gs[0].account_id : null;
    } catch(e){
      err.value = (e.data && e.data.error) || e.message || t('dashboard.inbox_err', 'Could not load the bank inbox.');
    }
    loading.value = false;
    syncVisible();
  }

  // Hide the whole panel when there is nothing to allocate (owner 2026-09-19: empty needs-action
  // panels must not render at all). The card starts hidden and is only revealed once load() finds
  // unallocated lines; allocating/deleting the last line hides it again via removeLine.
  function syncVisible(){
    const d = data.value;
    const hasWork = !err.value && d && (d.total || 0) > 0 && (d.groups || []).length > 0;
    card.hidden = !hasWork;
  }

  // A line just posted → drop it: decrement its group count and the grand total, and drop the group
  // once nothing is left to show. A NEW object is assigned so the reactive body re-renders (signals
  // compare by identity).
  function removeLine(accountId, lineId){
    const d = data.value; if(!d) return;
    let total = d.total || 0;
    const groups = [];
    for(const g of (d.groups || [])){
      if(g.account_id !== accountId){ groups.push(g); continue; }
      const lines = (g.lines || []).filter(x => x.id !== lineId);
      const removed = (g.lines || []).length - lines.length;
      total = Math.max(0, total - removed);
      const count = Math.max(0, (g.count || 0) - removed);
      if(!lines.length || count <= 0) continue;    // nothing left to show → drop the group
      groups.push({ ...g, lines, count });
    }
    data.value = { groups, total };
    // if the shown account's group just emptied out, fall back to the first remaining account
    if(!groups.some(g => g.account_id === selAcct.value)) selAcct.value = groups.length ? groups[0].account_id : null;
    syncVisible();               // last line allocated/deleted → hide the whole panel
  }

  // Searchable GL picker (cashbook showGlDrop pattern) — replaces the native <select>: a
  // [data-float-anchor] text input drives a floating cb-dropdown of the SAME candidate pool.
  // Typing filters; the shared dropnav wiring (glDropKeydown) gives ArrowUp/Down + Enter + Escape;
  // picking a row runs the identical allocate+post flow the <select> did. The input carries no
  // bound signal (choosing is terminal), so there is no focus-trap and no nested reactive slot.
  function showGlDrop(anchor, group, line){
    const q = anchor.value.trim().toLowerCase();
    closeFloat();
    const pool = glPool();
    const matches = (q
      ? pool.filter(a => formatGlCode(a.code).toLowerCase().includes(q) || String(a.name || '').toLowerCase().includes(q))
      : pool.slice()).slice(0, 50);
    const drop = document.createElement('div');
    drop.className = 'cb-dropdown';
    if(!matches.length){ drop.innerHTML = '<div class="cb-drop-item cb-drop-muted">' + t('bank_import.no_matches', 'No matches') + '</div>'; }
    matches.forEach(a => {
      const item = document.createElement('div');
      item.className = 'cb-drop-item';
      item.innerHTML = `<span><span class="mono">${formatGlCode(a.code)}</span> ${a.name}</span>`;
      item.onclick = () => { closeFloat(); allocateLine(group, line, { account_id: a.id }); };
      drop.appendChild(item);
    });
    positionNear(drop, anchor, 340);
    highlightFirst(drop);   // top match pre-highlighted → Enter picks it instantly (dropnav)
  }
  const glDropKeydown = e => dropdownKeydown(e, floatRoot.querySelector('.cb-dropdown'), { wrap: false, onEscape: closeFloat });

  // Debtor/Creditor dest tab → a floating contact picker (the cashbook contactStep pattern, on the
  // inbox's own float layer): search ref.contacts by is_customer/is_supplier and pick one to
  // allocate via {contact_id}. A typed name that isn't a contact yet offers "Create …" →
  // {new_contact_name, dest}, which the API turns into the contact (bank_rules_service.allocate_line).
  function showContactPop(anchor, group, line, kind){
    closeFloat();
    const r = ref.value || {};
    const pop = document.createElement('div');
    pop.className = 'alloc-pop';
    const head = document.createElement('div');
    head.className = 'alloc-pop-head';
    head.textContent = kind === 'D' ? t('bank_import.select_debtor', 'Select Debtor') : t('bank_import.select_creditor', 'Select Creditor');
    pop.appendChild(head);
    const cin = document.createElement('input');
    cin.type = 'text'; cin.className = 'form-control cb-in'; cin.autocomplete = 'off';
    cin.placeholder = t('cashbook.contact_search_ph', 'Search contact… e.g. Mountain');
    pop.appendChild(cin);
    const cl = document.createElement('div');
    cl.className = 'alloc-results';
    pop.appendChild(cl);
    const rc = () => {
      const q = cin.value.trim().toLowerCase();
      cl.innerHTML = '';
      ((r.contacts) || [])
        .filter(c => (kind === 'D' ? c.is_customer : c.is_supplier) &&
                     (!q || String(c.code || '').toLowerCase().includes(q) || String(c.name || '').toLowerCase().includes(q)))
        .slice(0, 8).forEach(c => {
          const item = document.createElement('div');
          item.className = 'cb-drop-item';
          item.innerHTML = `<span><span class="mono">${c.code || ''}</span> ${c.name || ''}</span>`;
          item.onclick = e => { e.stopPropagation(); closeFloat(); allocateLine(group, line, { contact_id: c.id }); };
          cl.appendChild(item);
        });
      const raw = cin.value.trim();
      if(raw){
        const add = document.createElement('div');
        add.className = 'cb-drop-item cb-drop-new';
        add.innerHTML = `<span>➕ ${t('cashbook.create_named', 'Create')} ${kind === 'D' ? t('dashboard.debtor', 'Debtor') : t('dashboard.creditor', 'Creditor')} “<b>${raw.replace(/</g, '&lt;')}</b>”</span>`;
        add.onclick = e => { e.stopPropagation(); closeFloat(); allocateLine(group, line, { new_contact_name: raw, dest: kind }); };
        cl.appendChild(add);
      }
      if(!cl.children.length) cl.innerHTML = '<div class="cb-drop-item cb-drop-muted">' + t('bank_import.no_matches', 'No matches') + '</div>';
      highlightFirst(cl);
    };
    cin.oninput = rc;
    cin.addEventListener('keydown', e => dropdownKeydown(e, cl, { wrap: false, onEscape: closeFloat }));
    rc();
    positionNear(pop, anchor, 340);
    setTimeout(() => cin.focus(), 30);
  }

  // Drop the row that is currently flashing green — NOW. Called by the hold timer, and up-front by
  // the next allocate so a second click never leaves an earlier row stuck in its success state.
  function finalizeOk(){
    if(!okId.value) return;
    clearTimeout(rowOkTimer);
    const id = okId.value, acct = okAcct;
    okId.value = 0; okAcct = 0;
    removeLine(acct, id);
  }

  // body is one terminal allocation target: {account_id} (General/GL) · {contact_id} (an existing
  // debtor/creditor) · {new_contact_name, dest} (create D/C inline) · {dest:'V'} (VAT). The API
  // (bank_rules_service.allocate_line) resolves each; save_as_rule + post are constant here.
  async function allocateLine(group, line, body){
    if(!body || busyId.value) return;              // one line at a time
    busyId.value = line.id; rowErr.value = ''; okMsg.value = '';
    try {
      const res = await api.post('/companies/' + companyId + '/bank-lines/' + line.id + '/allocate',
        { save_as_rule: true, apply_to_similar: false, post: true, ...body });   // body may set apply_to_similar
      busyId.value = 0;
      if(res && res.post_error) rowErr.value = res.post_error;   // posted-with-warning (allocation still succeeded)
      else {
        okMsg.value = (res && res.message) || t('dashboard.inbox_posted', 'Allocated and posted.');
        clearTimeout(okTimer); okTimer = setTimeout(() => { okMsg.value = ''; }, 3500);
      }
      // v2 bank-import feel: the row flashes green + ✓ and HOLDS, THEN drops (removeLine on the
      // timer, not synchronously) so an allocation reads as confirmed rather than silently vanishing.
      finalizeOk();                                // clear any earlier held row first
      okId.value = line.id; okAcct = group.account_id;
      clearTimeout(rowOkTimer); rowOkTimer = setTimeout(finalizeOk, OK_HOLD_MS);
    } catch(e){
      busyId.value = 0;
      rowErr.value = (e.data && e.data.error) || e.message || t('dashboard.inbox_alloc_failed', 'Could not allocate this line.');
    }
  }

  // ⇄ move a line between deposits/payments (register parity) — flips the sign, then reloads the worklist
  async function inboxFlip(group, line){
    if(busyId.value) return;
    busyId.value = line.id; rowErr.value = '';
    try { await api.post('/companies/' + companyId + '/bank-lines/' + line.id + '/flip', {}); busyId.value = 0; await load(); }
    catch(e){ busyId.value = 0; rowErr.value = (e.data && e.data.error) || e.message || t('dashboard.inbox_flip_failed', 'Could not move this line.'); }
  }
  // ✕ delete the unallocated bank line (register parity) — armed via inboxDelAsk, then drops the row
  async function inboxDelete(group, line){
    if(busyId.value) return;
    busyId.value = line.id; rowErr.value = '';
    try { await api.del('/companies/' + companyId + '/bank-lines/' + line.id); busyId.value = 0; inboxDelAsk.value = 0; removeLine(group.account_id, line.id); }
    catch(e){ busyId.value = 0; rowErr.value = (e.data && e.data.error) || e.message || t('common.delete_failed', 'Delete failed'); }
  }

  // The "+ Allocate GL Account" popover — the cashbook openAllocPop on the inbox's own float layer:
  // dest tabs (General/Debtors/Creditors/VAT) + AI suggestion + a searchable GL list, so the row reads
  // exactly like a register row (no inline dest pills / search box). Each choice is terminal → allocateLine.
  function openInboxAllocPop(anchor, group, line){
    closeFloat();
    const dests = destList();
    let applySimilar = true;   // register parity: the popover's "Apply to all similar" (default on for unallocated)
    const pop = document.createElement('div');
    pop.className = 'alloc-pop';
    const head = document.createElement('div');
    head.className = 'alloc-pop-head';
    head.textContent = t('cashbook.allocate_gl', 'Allocate GL Account');
    pop.appendChild(head);
    const search = document.createElement('input');
    search.type = 'text'; search.className = 'form-control cb-in'; search.autocomplete = 'off';
    search.placeholder = t('cashbook.gl_search_ph', 'Search GL account… e.g. Rent');
    const results = document.createElement('div');
    results.className = 'alloc-results';
    if(dests.length > 1){
      const destRow = document.createElement('div');
      destRow.className = 'alloc-dest-row';
      dests.forEach(([code, label]) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'cb-dest-pill' + (code === 'G' ? ' active' : ''); b.dataset.dest = code; b.textContent = label;
        b.onclick = e => {
          e.stopPropagation();
          destRow.querySelectorAll('.cb-dest-pill').forEach(p => p.classList.toggle('active', p === b));
          if(code === 'D' || code === 'C') showContactPop(anchor, group, line, code);
          else if(code === 'V'){ closeFloat(); allocateLine(group, line, { dest: 'V' }); }
          else search.focus();
        };
        destRow.appendChild(b);
      });
      pop.appendChild(destRow);
    }
    // the learner's suggestion (same merchant + branch history) when it has one, else the name-based guess
    const guess = line.learned ? null : scoreGL(line.description, glPool())[0];
    const sg = suggestionFor(line, guess);
    if(sg){
      const s = document.createElement('button');
      s.type = 'button'; s.className = 'cb-ai-chip' + (sg.rule ? ' cb-rule-chip' : sg.learned ? ' cb-learned-chip' : '');
      if(sg.learned) s.title = sg.reason;
      const tag = sg.rule ? t('bank.rule_tag', 'Rule') : sg.learned ? '✓ ' + sg.evidence : t('common.ai', 'AI') + ' ' + confPct(line.match_type === 'ai_suggest' ? line.ai_confidence : null, guess.score) + '%';
      s.innerHTML = '<span class="cb-ai-tag">' + tag + '</span> <span class="mono">' + formatGlCode(sg.code) + '</span> ' + String(sg.name || '').replace(/</g, '&lt;');
      s.onclick = e => { e.stopPropagation(); closeFloat(); allocateLine(group, line, { account_id: sg.account_id, apply_to_similar: applySimilar }); };
      pop.appendChild(s);
    }
    pop.appendChild(search);
    pop.appendChild(results);
    const opts = document.createElement('label');
    opts.className = 'check-line alloc-opts';
    opts.innerHTML = '<input type="checkbox" checked> ' + t('cashbook.apply_all_similar', 'Apply to all similar');
    opts.querySelector('input').onchange = e => { applySimilar = e.target.checked; };
    pop.appendChild(opts);
    const render = () => {
      const q = search.value.trim().toLowerCase();
      const gp = glPool();
      const matches = (q ? gp.filter(a => formatGlCode(a.code).toLowerCase().includes(q) || String(a.name || '').toLowerCase().includes(q)) : gp.slice()).slice(0, 50);
      results.innerHTML = '';
      if(!matches.length) results.innerHTML = '<div class="cb-drop-item cb-drop-muted">' + t('bank_import.no_matches', 'No matches') + '</div>';
      matches.forEach(a => {
        const item = document.createElement('div');
        item.className = 'cb-drop-item';
        item.innerHTML = '<span><span class="mono">' + formatGlCode(a.code) + '</span> ' + String(a.name || '').replace(/</g, '&lt;') + '</span>';
        item.onclick = e => { e.stopPropagation(); closeFloat(); allocateLine(group, line, { account_id: a.id, apply_to_similar: applySimilar }); };
        results.appendChild(item);
      });
      highlightFirst(results);
    };
    search.oninput = render;
    search.addEventListener('keydown', e => dropdownKeydown(e, results, { wrap: false, onEscape: closeFloat }));
    render();
    positionNear(pop, anchor, 340);
    setTimeout(() => search.focus(), 30);
  }

  // 🔎 "See this line on the statement" — the cashbook locate feature, brought to the inbox (owner
  // 2026-09-17: "the magnifying glass must also be usable on the dashboard"). Uses the line's
  // source_job_id + has_statement_image (both carried by the needs-attention row, register parity).
  function closeLocate(){
    locateSeq++;
    const s = locate.value;
    if(s && s.imgUrl){ try { URL.revokeObjectURL(s.imgUrl); } catch { /* already revoked */ } }
    locate.value = null;
  }
  // a page of the job's statement; `file` names which one when the job holds two dropped together (the locate says),
  // else the newest (3 Nov live: an OVK July line opened on the August statement)
  const locatePageUrl = (job, page, file) => '/companies/' + companyId + '/bank-imports/' + job + '/page?page=' + page
    + (file ? '&file=' + encodeURIComponent(file) : '');
  async function locateOnStatement(line){
    const job = line.source_job_id;
    if(!job) return;
    const seq = ++locateSeq;
    const title = (line.description || '').slice(0, 60) || t('cashbook.statement', 'Statement');
    locate.value = { status: 'loading', title, imgUrl: '', box: null, page: 0, job };
    let data;
    try {
      data = await api.post('/companies/' + companyId + '/bank-imports/' + job + '/locate',
        { date: line.txn_date, description: line.description || '', reference: line.reference || '', amount: line.amount });
    } catch(e){
      if(seq === locateSeq) locate.value = { status: 'error', title, error: (e.data && e.data.error) || t('cashbook.locate_failed', 'Could not reach the statement locator.') };
      return;
    }
    if(seq !== locateSeq) return;
    const errMap = {
      no_key: t('cashbook.locate_no_key', 'Add your Gemini API key in AI settings to locate lines on the statement.'),
      no_pages: t('cashbook.locate_no_source', 'The original statement image was not stored for this import (it predates this feature, or was a CSV).'),
    };
    if(data && data.error && errMap[data.error]){ locate.value = { status: 'error', title, error: errMap[data.error] }; return; }
    const pageCount = (data && data.page_count) || 1;
    const foundPage = (data && typeof data.page === 'number' && data.page >= 0 && data.page < pageCount) ? data.page : 0;
    let foundBox = (data && Array.isArray(data.box)) ? data.box : null;
    if(foundBox && !(foundBox.length === 4 && foundBox[0] >= 0 && foundBox[2] <= 1 && foundBox[2] > foundBox[0] && (foundBox[2] - foundBox[0]) <= 0.5)) foundBox = null;
    const file = (data && data.file) || '';
    let imgUrl = '';
    try {
      imgUrl = await api.objectUrl(locatePageUrl(job, foundPage, file));
    } catch(e){
      if(seq === locateSeq) locate.value = { status: 'error', title, error: (e.data && e.data.error) || t('cashbook.locate_no_image', 'Could not load the statement page image.') };
      return;
    }
    if(seq !== locateSeq){ try { URL.revokeObjectURL(imgUrl); } catch { /* stale */ } return; }
    locate.value = { status: 'ready', title, job, file, imgUrl, box: foundBox, page: foundPage, pageCount, foundPage, foundBox };
    setTimeout(() => { const el = document.querySelector('.cb-stmt-hl'); if(el && el.scrollIntoView) el.scrollIntoView({ block: 'center' }); }, 250);
  }
  async function gotoLocatePage(n){
    const s = locate.value;
    if(!s || s.status !== 'ready' || !s.job) return;
    const page = Math.max(0, Math.min((s.pageCount || 1) - 1, n));
    if(page === s.page) return;
    const seq = ++locateSeq;
    let imgUrl;
    try { imgUrl = await api.objectUrl(locatePageUrl(s.job, page, s.file)); }
    catch { return; }
    const cur = locate.value;
    if(!cur || seq !== locateSeq){ try { URL.revokeObjectURL(imgUrl); } catch { /* stale */ } return; }
    if(cur.imgUrl){ try { URL.revokeObjectURL(cur.imgUrl); } catch { /* already revoked */ } }
    locate.value = { ...cur, imgUrl, page, box: (page === cur.foundPage ? cur.foundBox : null) };
    if(page === cur.foundPage) setTimeout(() => { const el = document.querySelector('.cb-stmt-hl'); if(el && el.scrollIntoView) el.scrollIntoView({ block: 'center' }); }, 200);
  }
  function locateModal(s){
    let band = '';
    if(s.box){
      const pad = 1.2;
      const top = Math.max(0, s.box[0] * 100 - pad);
      const h = Math.min(100 - top, (s.box[2] - s.box[0]) * 100 + pad * 2);
      band = `--hl-top:${top}%;--hl-h:${h}%`;
    }
    const pgnav = s.status === 'ready' && s.pageCount > 1 ? html`<span class="cb-stmt-nav">
        <button class="cb-stmt-pg" ?disabled=${s.page <= 0} title=${t('cashbook.prev_page', 'Previous page')} @click=${() => gotoLocatePage(s.page - 1)}>‹</button>
        <span class="cb-stmt-page mono">p${(s.page || 0) + 1}/${s.pageCount}</span>
        <button class="cb-stmt-pg" ?disabled=${s.page >= s.pageCount - 1} title=${t('cashbook.next_page', 'Next page')} @click=${() => gotoLocatePage(s.page + 1)}>${icon('chevron-right', {size: 14})}</button>
      </span>` : '';
    return html`<div class="cb-stmt-overlay" @click=${e => { if(e.target === e.currentTarget) closeLocate(); }}>
      <div class="cb-stmt-modal" role="dialog" aria-label="Statement source">
        <div class="cb-stmt-head">
          <span class="fw-700 cb-stmt-title">${s.title}</span>
          ${pgnav}
          <span class="cb-mode-spacer"></span>
          <button class="cb-clear-btn" title=${t('common.close', 'Close')} @click=${closeLocate}>✕</button>
        </div>
        <div class="cb-stmt-body">
          ${s.status === 'loading'
            ? html`<div class="cb-stmt-locating">${t('cashbook.locating', 'Asking Sleek to find this line on the statement…')}</div>`
            : s.status === 'error'
              ? html`<div class="muted-block cb-stmt-err">${s.error}</div>`
              : html`<div class="cb-stmt-img-wrap">
                  <img class="cb-stmt-img" src=${s.imgUrl} alt="Statement page">
                  ${s.box ? html`<div class="cb-stmt-hl" style=${band}></div>` : ''}
                </div>
                ${!s.box ? html`<div class="cb-stmt-noline">${s.pageCount > 1
                    ? t('cashbook.locate_noline_multi', 'Showing the statement — the exact line couldn’t be pinpointed. Use ‹ › to look through the pages.')
                    : t('cashbook.locate_noline', 'Showing the statement — the exact line could not be pinpointed automatically.')}</div>` : ''}`}
        </div>
      </div>
    </div>`;
  }

  // One worklist line, rendered EXACTLY like the bank-account register's unallocated row (owner
  // 2026-09-17: "identical") — same cb-table columns, the "+ Allocate GL Account" pill (opens the
  // dest/GL popover), the AI chip, 🔎/✎/⇄/✕ actions, coloured received/paid, an Auto badge and balance.
  const lineRow = (group, line, pool, busy, okRow, sym, delAsk) => {
    const isBusy = busy === line.id;
    const isOk = okRow === line.id;                   // just allocated → green + ✓, about to drop
    const amt = Number(line.amount) || 0;
    const guess = line.learned ? null : scoreGL(line.description, pool)[0];   // name-based guess only without history
    const sg = suggestionFor(line, guess);            // the learner's suggestion (merchant + branch history) first
    const bal = line.computed_balance ?? line.running_balance;   // register parity: the computed continuation (null raw snapshot on manual lines)
    const del = delAsk === line.id;
    return html`<tr class=${'cb-line cb-row-unalloc bank-inbox-row' + (isBusy ? ' is-busy' : '') + (isOk ? ' bank-inbox-ok-row' : '')}>
      <td class="mono cb-td-date" data-label=${t('common.date', 'Date')}>${formatDate(line.txn_date, df())}${line.is_duplicate ? html` <span class="cb-dup" title=${t('dashboard.inbox_dup', 'Looks like a duplicate — check before allocating')}>⚠</span>` : ''}${line.is_gap ? html` <span class="cb-dup" title=${t('dashboard.inbox_gap', 'A balance gap sits by this line — verify it')}>⚠</span>` : ''}</td>
      <td class="cb-td-ref mono" data-label=${t('common.ref', 'Ref')}>${line.reference || ''}</td>
      <td class="cb-td-desc" data-label=${t('common.description', 'Description')}>${line.description || t('dashboard.inbox_no_desc', '(no description)')}<br><span class="cb-alloc-line">
        <button class="alloc-btn" data-float-anchor ?disabled=${isBusy} @click=${e => { e.stopPropagation(); openInboxAllocPop(e.target, group, line); }}>+ ${t('cashbook.allocate_gl', 'Allocate GL Account')}</button>
        ${sg ? html`<button class=${'cb-ai-chip' + (sg.rule ? ' cb-rule-chip' : sg.learned ? ' cb-learned-chip' : '')} ?disabled=${isBusy} title=${sg.learned ? sg.reason : t('dashboard.inbox_suggested', 'Suggested — click to allocate and post')} @click=${e => { e.stopPropagation(); allocateLine(group, line, { account_id: sg.account_id }); }}><span class="cb-ai-tag">${sg.rule ? t('bank.rule_tag', 'Rule') : sg.learned ? '✓ ' + sg.evidence : t('common.ai', 'AI') + ' ' + confPct(line.match_type === 'ai_suggest' ? line.ai_confidence : null, guess.score) + '%'}</span> <span class="mono">${formatGlCode(sg.code)}</span> ${sg.name}</button>` : ''}
      </span>${line.moved_to ? html`<div class="cb-moved-note">↗ ${movedOutText(line.moved_to, t)}</div>` : ''}</td>
      <td class="cb-td-act2" data-label=${t('common.actions', 'Actions')}>${isOk ? html`<span class="bank-inbox-ok-tick">✓ ${t('dashboard.inbox_allocated', 'Allocated')}</span>` : html`<span class="cb-row-actions"><button class=${'cb-locate' + (line.has_statement_image ? '' : ' cb-locate-off')} title=${t('cashbook.locate_hint', 'See where this line came from on the statement')} ?disabled=${isBusy || !line.has_statement_image} @click=${() => { if(line.has_statement_image) locateOnStatement(line); }}>🔎</button><span class="cb-act-edit">
        <button class="cb-flip" title=${t('common.edit', 'Edit')} ?disabled=${isBusy} @click=${() => nav(co('/bank/' + group.account_id))}>✎</button>
        <button class="cb-flip" title=${amt >= 0 ? t('cashbook.to_payments_out', 'Move to payments (out)') : t('cashbook.to_deposits_in', 'Move to deposits (in)')} ?disabled=${isBusy} @click=${() => inboxFlip(group, line)}>⇄</button>
        ${del ? html`<span class="confirm-inline" @click=${e => e.stopPropagation()}><span class="confirm-q">${t('common.delete', 'Delete')}?</span><button class="btn-confirm-yes" ?disabled=${isBusy} @click=${() => inboxDelete(group, line)}>${t('common.yes', 'Yes')}</button><button class="btn-confirm-no" @click=${() => { inboxDelAsk.value = 0; }}>${t('common.no', 'No')}</button></span>` : html`<button class="del-btn" title=${t('common.delete', 'Delete')} @click=${() => { inboxDelAsk.value = line.id; }}>✕</button>`}
      </span></span>`}</td>
      <td class="mono text-right t-in cb-td-received" data-label=${t('cashbook.received', 'Received')}>${amt >= 0 ? fmtMoney(amt, sym) : ''}</td>
      <td class="mono text-right t-out cb-td-paid" data-label=${t('cashbook.paid', 'Paid')}>${amt < 0 ? fmtMoney(amt, sym) : ''}</td>
      <td class="cb-td-source" data-label=${t('common.source', 'Source')}>${line.statement_id ? html`<span class="cb-src cb-src-auto">${t('cashbook.mode_auto', 'Auto')}</span>` : html`<span class="cb-src cb-src-manual">${t('cashbook.mode_manual', 'Manual')}</span>`}</td>
      <td class="mono text-right cb-td-balance" data-label=${t('common.balance', 'Balance')}>${bal != null ? html`<span class=${bal >= 0 ? 't-in' : 't-out'}>${fmtMoney(bal, sym, true)}</span>` : ''}</td>
    </tr>`;
  };

  const groupBlock = (g, pool, busy, okRow, delAsk) => {
    const shown = (g.lines || []).length;
    const more = (g.count || 0) - shown;
    const sym = g.currency_symbol || baseCurrencySymbol();
    return html`<div class="bank-inbox-group">
      <div class="bank-inbox-grp-head">
        <span class="bank-inbox-acct">${g.account_name || t('dashboard.inbox_account', 'Bank account')}</span>
        ${g.bank_name ? html`<span class="dash-tri-banks">${g.bank_name}</span>` : ''}
        <span class="dash-tri-count">${(g.count || 0) + ' ' + t('dashboard.inbox_need', 'need allocation')}</span>
        <button class="btn sm ghost" title=${t('dashboard.inbox_open_hint', 'Open this account in the cashbook')}
          @click=${() => nav(co('/bank/' + g.account_id))}>${t('dashboard.inbox_open', 'Open account')} ${icon('chevron-right', {size: 14})}</button>
      </div>
      <div class="table-wrap cb-register-wrap"><table class="cb-table cb-register-table bank-inbox-table">
        <colgroup><col class="bic-date"><col class="bic-ref"><col class="bic-desc"><col class="bic-act"><col class="bic-amt"><col class="bic-amt"><col class="bic-src"><col class="bic-bal"></colgroup>
        <thead><tr>
          <th>${t('common.date', 'Date')}</th><th>${t('common.ref', 'Ref')}</th><th>${t('common.description', 'Description')}</th>
          <th class="cb-th-act"></th>
          <th class="text-right">${t('cashbook.received', 'Received')}</th><th class="text-right">${t('cashbook.paid', 'Paid')}</th>
          <th>${t('common.source', 'Source')}</th><th class="text-right">${t('common.balance', 'Balance')}</th>
        </tr></thead>
        <tbody>${(g.lines || []).map(l => lineRow(g, l, pool, busy, okRow, sym, delAsk))}</tbody>
      </table></div>
      ${more > 0 ? html`<div class="dash-tri-meta bank-inbox-more">${'+ ' + more + ' ' + t('dashboard.inbox_more', 'more — open the account to allocate the rest')}</div>` : ''}
    </div>`;
  };

  const inboxBody = () => {
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    const d = data.value;
    const groups = (d && d.groups) || [];
    const total = (d && d.total) || 0;
    if(!total || !groups.length)
      return html`<div class="muted-block">${t('dashboard.inbox_empty', 'All bank lines allocated — nothing needs attention.')}</div>`;
    const pool = glPool(), busy = busyId.value, rErr = rowErr.value, ok = okMsg.value, okRow = okId.value,
          delAsk = inboxDelAsk.value, loc = locate.value;   // read here so arming ✕ / opening statement source re-renders
    // Owner huddle 2 (2026-09-17): NO per-account tabs — every account's unallocated lines flow
    // straight DOWN, each account under its own header (groupBlock carries the header) over a cb-table
    // whose rows are IDENTICAL to the bank-account register's unallocated rows. Plain .map in one render
    // (the whole inboxBody re-renders as a unit) — no nested ${()=>…}, so the append gotcha can't bite.
    return html`
      ${rErr ? html`<div class="row-err">${rErr}</div>` : ''}
      ${ok ? html`<div class="dash-tri-done-msg bank-inbox-ok">✓ ${ok}</div>` : ''}
      <div class="bank-inbox-groups">${groups.map(g => groupBlock(g, pool, busy, okRow, delAsk))}</div>
      ${loc ? locateModal(loc) : ''}`;
  };

  const headerCount = () => {
    if(loading.value || err.value) return '';
    const d = data.value, n = (d && d.total) || 0;
    return n ? (n + ' ' + t('dashboard.inbox_count', 'to allocate')) : '';
  };

  const folded = collOn('bankinbox');
  const card = document.createElement('div');
  card.className = 'card bank-inbox dash-coll' + (folded ? ' is-collapsed' : '');
  card.setAttribute('data-coll', 'bankinbox');
  card.hidden = true;            // stays hidden until load() finds lines to allocate (hide-when-empty)
  card.append(html`
    <h3 class="coll-toggle" role="button" tabindex="0" aria-expanded=${folded ? 'false' : 'true'}
      title=${t('dashboard.coll_hint', 'Collapse or expand this block')} @click=${toggleColl} @keydown=${collKey}><span class="coll-ico">${folded ? icon('chevron-right', {size: 12}) : icon('chevron-down', {size: 12})}</span>${t('dashboard.inbox_title', 'Bank inbox')} <small>${t('dashboard.inbox_hint', 'unallocated bank lines across your accounts — allocate without opening each one')}</small><span class="coll-n">${() => headerCount()}</span></h3>
    <div class="coll-body">${() => inboxBody()}</div>`);
  load();
  return card;
}

export function tenantDashboardScreen(companyId){
  const data = signal(null), loading = signal(true), err = signal('');
  // Every dashboard→elsewhere click goes through go() so the destination can offer a
  // "‹ Back to dashboard" button that restores this scroll position (src/shared/dashnav.js).
  const go = dest => dashGo(companyId, dest);
  const co = path => '/co/' + companyId + path;

  // Setup guide, sales-by-month + receivables-aging charts, and the scan-a-document chooser were
  // all removed from the dashboard in the 2026-09-19 redesign.

  // ── Bank-allocation triage (v2 wave feature; iT has no bank) — retitled so it can't be
  // confused with the iT "Needs your attention" worklist above it. Machinery unchanged.
  const triage = signal(null), triBusy = signal(''), triErr = signal('');
  const doneMap = signal({});            // group key → {updated} success state (with Undo)
  const allDone = signal(false);
  const doneTimers = {};
  const gKey = g => g.group_desc + '|' + g.direction;
  // "‹ Back to dashboard": restore the remembered scroll. Applied after the MAIN data renders
  // AND again after the async triage panel lands (it reflows the page top).
  let backY = null;
  function applyBackScroll(){
    if(backY == null) return;
    const y = backY;
    let tries = 0;
    const tick = () => {
      window.scrollTo({ top: y, behavior: 'instant' });
      if(Math.abs(window.scrollY - y) > 2 && ++tries < 20) setTimeout(tick, 50);
    };
    tick();
  }
  async function loadTriage(){
    try {
      const had = triage.value && (triage.value.groups || []).length > 0;
      const tri = await api.get('/companies/' + companyId + '/dashboard/unallocated');
      triage.value = tri;
      doneMap.value = {};
      if(had && !(tri.groups || []).length){    // v2: badge → "All done!" banner at 0
        allDone.value = true;
        setTimeout(() => { allDone.value = false; }, 6000);
      }
    } catch(e){ triErr.value = (e.data && e.data.error) || t('dashboard.err_alloc_load', 'Could not load allocations'); }
    applyBackScroll();               // the triage panel just reflowed the page — re-place
    backY = null;                    // consumed: later triage reloads must not jump the scroll
  }
  async function load(){
    loading.value = true;
    try {
      const d = await api.get('/companies/' + companyId + '/dashboard');
      // A tab opened straight onto the dashboard may not know the company's mark yet (/me still on its
      // way): the payload names the base currency, so no base figure renders without its mark.
      const co = (d && d.company) || {};
      if(!baseCurrencySymbol() && co.currency_symbol) setBaseCurrencySymbol(co.currency_symbol);
      if(!baseCurrencyCode() && co.currency_code) setBaseCurrencyCode(co.currency_code);
      data.value = d;
      err.value = '';
      // triageView() retired — superseded by the cross-account Bank inbox (bankInboxSection),
      // which allocates AND posts (≤2 clicks). loadTriage()/triageView left in place, unrendered.
    }
    catch(e){ err.value = (e.data && e.data.error) || t('dashboard.err_load', 'Could not load the dashboard'); }
    loading.value = false;
    backY = takePendingDashScroll();
    applyBackScroll();               // first placement; loadTriage re-applies on its reflow
  }
  load();

  // ── "Needs action" zone (below the bank inbox): drafts to send/confirm, then batches to release.
  // Each self-fetches into its own signal, and its panel hides entirely when it has nothing to show
  // (owner 2026-09-19: no empty needs-action cards).
  const draftsData = signal(null), draftsErr = signal('');
  let draftsCur = null;          // { byId, base } — the currency each draft is in (see draftsView)
  (async () => {
    try {
      const r = await api.get('/companies/' + companyId + '/documents?per_page=500');
      // "draft" = not issued yet (needs confirming); "issued" = finalised/numbered but not sent yet.
      const rows = (r.data || []).filter(d => d.status === 'draft' || d.status === 'issued');
      // a row that names its currency only by id: look it up before the list renders (owner 2026-10-19)
      if(needsCurrencyLookup(rows)) draftsCur = await companyCurrencies(companyId);
      draftsData.value = rows;
    } catch(e){ draftsErr.value = (e.data && e.data.error) || t('dashboard.drafts_err', 'Could not load drafts'); }
  })();
  const openDraft = d => { const key = TYPE_KEY[d.type_code]; if(key) go(co('/' + key + '/' + d.id + '/edit')); };

  const batchesData = signal(null);
  (async () => {
    try { const r = await api.get('/companies/' + companyId + '/batches/due'); batchesData.value = (r && r.data) || []; }
    catch { batchesData.value = []; }        // no due batches / endpoint error → panel simply hides
  })();

  // floating popover layer (cashbook pattern — imperative so typing never re-renders)
  const floatRoot = mountFloatRoot();   // shared layer — ONE document listener app-wide
  const closeFloat = closeFloatRoot;
  function positionNear(el, anchor, width){
    const r = anchor.getBoundingClientRect();
    el.style.position = 'fixed';
    el.style.left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) + 'px';
    el.style.zIndex = 300;
    floatRoot.appendChild(el);
    const h = el.offsetHeight, below = window.innerHeight - r.bottom - 8;
    el.style.top = (below >= h ? r.bottom + 4 : Math.max(8, r.top - h - 4)) + 'px';
  }

  async function assignGroup(group, body, ccId){
    const key = gKey(group);
    triBusy.value = key; triErr.value = '';
    try {
      const res = await api.post('/companies/' + companyId + '/bank-lines/' + group.sample_id + '/allocate',
        { ...body, save_as_rule: true, apply_to_similar: true, cost_centre_id: ccId || null });
      doneMap.value = { ...doneMap.value, [key]: { updated: res.updated || 1 } };
      clearTimeout(doneTimers[key]);
      doneTimers[key] = setTimeout(() => loadTriage(), 4000);   // v2: done card fades after 4s
    } catch(e){ triErr.value = (e.data && e.data.error) || t('dashboard.err_allocate', 'Could not allocate'); }
    triBusy.value = '';
  }
  async function undoGroup(group){
    clearTimeout(doneTimers[gKey(group)]);
    triErr.value = '';
    try {
      await api.post('/companies/' + companyId + '/bank-lines/undo-allocation',
        { description: group.description });
      await loadTriage();
    } catch(e){ triErr.value = (e.data && e.data.error) || t('dashboard.err_undo', 'Could not undo'); }
  }

  // optional cost-code step between pick and assign (v2 triage costcode step)
  function ccGate(group, body, anchor){
    const tri = triage.value || {};
    const ccs = tri.cost_centres || [];
    if(!ccs.length) return assignGroup(group, body);
    closeFloat();
    const pop = document.createElement('div');
    pop.className = 'alloc-pop';
    pop.innerHTML = '<div class="alloc-pop-head">' + t('dashboard.cost_code_optional', 'Cost code (optional)') + '</div>';
    const cin = document.createElement('input');
    cin.type = 'text'; cin.className = 'form-control cb-in';
    cin.placeholder = t('dashboard.filter_cost_ph', 'Filter cost codes… e.g. JHB'); cin.autocomplete = 'off';
    pop.appendChild(cin);
    const list = document.createElement('div');
    list.className = 'alloc-results';
    pop.appendChild(list);
    const render = q => {
      list.innerHTML = '';
      const none = document.createElement('div');
      none.className = 'cb-drop-item';
      none.textContent = t('dashboard.no_cost_code', '— No cost code —');
      none.onclick = e => { e.stopPropagation(); closeFloat(); assignGroup(group, body); };
      list.appendChild(none);
      ccs.filter(c => !q || String(c.code).toLowerCase().includes(q) ||
                      String(c.name || '').toLowerCase().includes(q))
        .slice(0, 10).forEach(c => {
          const item = document.createElement('div');
          item.className = 'cb-drop-item';
          item.innerHTML = `<span><span class="mono">${c.code}</span> ${c.name || ''}</span>`;
          item.onclick = e => { e.stopPropagation(); closeFloat(); assignGroup(group, body, c.id); };
          list.appendChild(item);
        });
      highlightFirst(list);
    };
    cin.oninput = () => render(cin.value.trim().toLowerCase());
    cin.addEventListener('keydown', e => dropdownKeydown(e, list, { wrap: false, onEscape: closeFloat }));
    render('');
    positionNear(pop, anchor, 300);
    setTimeout(() => cin.focus(), 30);
  }

  // the "More…" pill: inline GL search (control accounts tagged; D/C step to a contact)
  function openMorePop(group, anchor){
    closeFloat();
    const tri = triage.value || {};
    const pool = tri.alloc_accounts || [];
    const pop = document.createElement('div');
    pop.className = 'alloc-pop';
    pop.innerHTML = '<div class="alloc-pop-head">' + t('dashboard.allocate_to', 'Allocate to…') + '</div>';
    const sin = document.createElement('input');
    sin.type = 'text'; sin.className = 'form-control cb-in';
    sin.placeholder = t('cashbook.gl_search_ph', 'Search GL account… e.g. Rent'); sin.autocomplete = 'off';
    pop.appendChild(sin);
    const list = document.createElement('div');
    list.className = 'alloc-results';
    pop.appendChild(list);

    function contactStep(kind){
      pop.innerHTML = '';
      const head = document.createElement('div');
      head.className = 'alloc-pop-head';
      head.textContent = kind === 'D' ? t('dashboard.select_debtor', 'Select Debtor') : t('dashboard.select_creditor', 'Select Creditor');
      pop.appendChild(head);
      const cin = document.createElement('input');
      cin.type = 'text'; cin.className = 'form-control cb-in';
      cin.placeholder = t('cashbook.contact_search_ph', 'Search contact… e.g. Mountain'); cin.autocomplete = 'off';
      pop.appendChild(cin);
      const cl = document.createElement('div');
      cl.className = 'alloc-results';
      pop.appendChild(cl);
      const rc = q => {
        cl.innerHTML = '';
        ((kind === 'D' ? tri.debtors : tri.creditors) || [])
          .filter(c => !q || String(c.code || '').toLowerCase().includes(q) ||
                       String(c.name || '').toLowerCase().includes(q))
          .slice(0, 8).forEach(c => {
            const item = document.createElement('div');
            item.className = 'cb-drop-item';
            item.innerHTML = `<span><span class="mono">${c.code || ''}</span> ${c.name}</span>`;
            item.onclick = e2 => { e2.stopPropagation(); closeFloat(); ccGate(group, { contact_id: c.id }, anchor); };
            cl.appendChild(item);
          });
        if(!cl.children.length) cl.innerHTML = '<div class="cb-drop-item cb-drop-muted">' + t('dashboard.no_matches', 'No matches') + '</div>';
        highlightFirst(cl);
      };
      cin.oninput = () => rc(cin.value.trim().toLowerCase());
      cin.addEventListener('keydown', e2 => dropdownKeydown(e2, cl, { wrap: false, onEscape: closeFloat }));
      rc('');
      setTimeout(() => cin.focus(), 30);
    }

    const render = q => {
      list.innerHTML = '';
      if(!q) return;
      pool.filter(a => String(a.code).toLowerCase().includes(q) ||
                       String(a.name || '').toLowerCase().includes(q))
        .slice(0, 8).forEach(a => {
          const tag = controlTag(a);
          const item = document.createElement('div');
          item.className = 'cb-drop-item';
          item.innerHTML = `<span><span class="mono">${gcode(a.code)}</span> ${a.name}` +
            (tag ? ` <span class="cb-ctl-tag cb-ctl-${tag.toLowerCase()}">${tag}</span>` : '') + '</span>';
          item.onclick = e2 => {
            e2.stopPropagation();
            if(tag === 'D' || tag === 'C') contactStep(tag);
            else if(tag === 'V'){ closeFloat(); ccGate(group, { dest: 'V' }, anchor); }
            else { closeFloat(); ccGate(group, { account_id: a.id }, anchor); }
          };
          list.appendChild(item);
        });
      if(!list.children.length) list.innerHTML = '<div class="cb-drop-item cb-drop-muted">' + t('dashboard.no_matches', 'No matches') + '</div>';
      highlightFirst(list);   // top match pre-highlighted → Enter picks it instantly
    };
    sin.oninput = () => render(sin.value.trim().toLowerCase());
    sin.addEventListener('keydown', e => dropdownKeydown(e, list, { wrap: false, onEscape: closeFloat }));
    positionNear(pop, anchor, 340);
    setTimeout(() => sin.focus(), 30);
  }

  const df = () => ((data.value && data.value.company && data.value.company.date_format) || 'dd/MM/yyyy');
  const gcode = c => formatGlCode(c);
  // Balance-type figures retain their accounting sign while the shared tone makes direction
  // scannable: positive green, negative red, zero neutral.
  const num = v => html`<span class=${totalTone(v)}>${fmtRS(v)}</span>`;
  // …and a figure in a given currency (a bank account's own balance)
  const numIn = (v, sym) => html`<span class=${totalTone(v)}>${fmtMoney(v, sym, true)}</span>`;

  // Every recent-activity row opens the relevant place: a bank-sourced line → its bank account,
  // a journal → the Journals screen, anything else → the full transaction listing.
  const activityDest = r => {
    if(r.bank_account_id) return co('/bank/' + r.bank_account_id);
    if((r.source || '').toLowerCase().includes('journal')) return co('/journals');
    return co('/reports/transactions');
  };
  const activityTitle = r => r.bank_account_id ? t('dashboard.open_bank_account', 'Open this bank account')
    : (r.source || '').toLowerCase().includes('journal') ? t('dashboard.open_journals', 'Open in Journals')
    : t('dashboard.open_txns', 'Open in the transaction listing');

  // The old "Needs your attention" worklist and the "Client activity" feed were removed in the
  // 2026-09-19 redesign; the drafts-to-confirm and batches-to-release panels replace them.

  // ── KPI tiles (iT .kpi language) — the seven iT receivables tiles plus the wave's own
  // accounting tiles (Total Cash / Creditors / Net Profit / VAT) in the same tile form.
  const kpi = (label, value, sub, accent, dest, opts) => {
    const numericValue = Number(value);
    const signedAccent = Number.isFinite(numericValue) && numericValue !== 0
      ? (numericValue > 0 ? 'ok' : 'bad') : null;
    const tone = accent || signedAccent;
    return html`<div
      class=${'kpi' + (tone ? ' kpi-' + tone : '') + (dest ? ' clickable' : '')}
      role=${dest ? 'button' : ''} tabindex=${dest ? '0' : ''}
      title=${dest ? t('dashboard.kpi_hint', 'Show the documents that make up this figure') : ''}
      @keydown=${e => { if(dest && (e.key === 'Enter' || e.key === ' ')){ e.preventDefault(); go(dest); } }}
      @click=${() => { if(dest) go(dest); }}>
    <div class="kpi-label">${label}</div>
    <div class="kpi-value num">${(opts && opts.plain) ? value : fmtR0(value)}</div>
    ${sub ? html`<div class="kpi-sub">${sub}</div>` : ''}</div>`;
  };

  const kpisView = () => {
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    const d = data.value; if(!d) return html`<div class="muted-block">${t('reports.no_data', 'No data.')}</div>`;
    const k = d.kpis, rv = d.receivables || {};
    const cnt = (n, one, many) => n + ' ' + (n === 1 ? one : many);
    const inv = co('/invoices');
    return collCard('kpis', t('dashboard.kpis_title', 'Key figures'),
      t('dashboard.kpis_hint', 'tap any tile to see exactly the documents behind the number'), '',
      html`<div class="kpis">
      ${kpi(t('dashboard.outstanding', 'Outstanding'), rv.outstanding || 0, cnt(rv.unpaid_count || 0, t('dashboard.unpaid_one', 'unpaid'), t('dashboard.unpaid_many', 'unpaid')), null, inv + '?focus=outstanding')}
      ${kpi(t('dashboard.overdue', 'Overdue'), rv.overdue || 0, cnt(rv.overdue_count || 0, t('dashboard.invoice_one', 'invoice'), t('dashboard.invoice_many', 'invoices')), (rv.overdue || 0) > 0 ? 'bad' : null, inv + '?focus=overdue')}
      ${kpi(t('dashboard.paid_month', 'Paid this month'), rv.paid_month || 0, rv.month_label || '', (rv.paid_month || 0) > 0 ? 'ok' : null, inv + '?focus=paidmonth')}
      ${kpi(t('dashboard.drafts', 'Drafts'), String(rv.drafts || 0), t('dashboard.drafts_sub', 'not yet issued'), null, inv + '?focus=draft', { plain: true })}
      ${kpi(t('dashboard.bank_accounts', 'Bank Accounts'), k.total_cash, cnt(k.bank_count || 0, t('dashboard.account_one', 'account'), t('dashboard.account_many', 'accounts')), null, co('/bank'))}
      ${kpi(t('dashboard.creditors', 'Creditors'), k.creditors, t('dashboard.you_owe', 'You owe'), null, co('/bills?focus=owing'))}
      ${kpi(t('dashboard.net_profit', 'Net Profit') + ' (' + t('dashboard.this_month', 'This Month') + ')', k.net_profit_month, '', Number(k.net_profit_month) < 0 ? 'bad' : 'ok', co('/reports/income-statement'))}
      ${kpi(t('dashboard.net_profit', 'Net Profit') + ' (' + t('reports.financial_year', 'Financial Year') + ')', k.net_profit_year, '', Number(k.net_profit_year) < 0 ? 'bad' : 'ok', co('/reports/income-statement'))}
      ${kpi(t('dashboard.vat', 'VAT') + ' ' + (k.vat < 0 ? t('dashboard.refund_due', 'Refund Due') : t('dashboard.payable', 'Payable')), Math.abs(Number(k.vat || 0)), '', k.vat < 0 ? 'ok' : k.vat > 0 ? 'bad' : null, (d.company && d.company.vat_registered) ? co('/reports/vat') : '')}
    </div>`);
  };

  // One v2 triage GROUP card: IN/OUT badge · N× · date range · description · total ·
  // suggestion pills (learned-rule ✓ first, then top-3 scoreGL, then More…).
  const groupCard = (g, tri) => {
    const key = gKey(g), done = doneMap.value[key], busy = triBusy.value === key;
    const dmy = iso => formatDate(iso, df());
    if(done) return html`<div class="dash-tri-card dash-tri-done">
      <span class="dash-tri-done-msg">✓ ${t('dashboard.allocated', 'Allocated')} ${done.updated} ${done.updated === 1 ? t('dashboard.entry_one', 'entry') : t('dashboard.entry_many', 'entries')} — ${g.description}</span>
      <button class="btn btn-link" @click=${() => undoGroup(g)}>${t('common.undo', 'Undo')} ↩</button>
    </div>`;
    const desc = (g.description || '').toUpperCase();
    const rule = (tri.rules || []).find(r => r.match_stem && desc.includes(String(r.match_stem).toUpperCase()));
    const ruleAccount = rule && rule.account_id
      ? (tri.alloc_accounts || []).find(a => a.id === rule.account_id) : null;
    const suggs = scoreGL(g.description, tri.accounts || [], tri.gl_usage)
      .filter(s => !ruleAccount || s.account.id !== ruleAccount.id).slice(0, 3);
    const dates = g.first_date === g.last_date ? dmy(g.first_date)
      : dmy(g.first_date) + ' — ' + dmy(g.last_date);
    return html`<div class=${'dash-tri-card' + (busy ? ' is-busy' : '')}>
      <div class="dash-tri-top">
        <span class=${'dash-tri-io ' + (g.direction === 'in' ? 'io-in' : 'io-out')}>${g.direction === 'in' ? t('dashboard.in', 'IN') : t('dashboard.out', 'OUT')}</span>
        <span class="dash-tri-xcount">${g.entry_count}×</span>
        <span class="dash-tri-meta mono">${dates}</span>
        <span class="dash-tri-banks">${(g.bank_names || []).join(' · ')}</span>
        <span class=${'dash-tri-amt mono' + (g.direction === 'out' ? ' num-neg' : '')}>${fmtRS(g.total_amount)}</span>
      </div>
      <div class="dash-tri-desc">${g.description || t('dashboard.no_description', '(no description)')}</div>
      <div class="dash-tri-pills">
        ${rule && ruleAccount ? html`<button class="alloc-pill-sug alloc-pill-rule" data-float-anchor ?disabled=${busy}
          title=${'Matches your rule "' + rule.match_stem + '"'}
          @click=${e => ccGate(g, { account_id: ruleAccount.id }, e.currentTarget)}>
          <span class="mono">${gcode(ruleAccount.code)}</span> ${ruleAccount.name} <span class="t-in">✓</span></button>` : []}
        ${rule && !ruleAccount && rule.contact_id ? html`<button class="alloc-pill-sug alloc-pill-rule" data-float-anchor ?disabled=${busy}
          title=${'Matches your rule "' + rule.match_stem + '"'}
          @click=${e => ccGate(g, { contact_id: rule.contact_id }, e.currentTarget)}>
          ${rule.contact_name || t('cashbook.contact', 'Contact')} <span class="t-in">✓</span></button>` : []}
        ${suggs.map(s => html`<button class="alloc-pill-sug" data-float-anchor ?disabled=${busy}
          @click=${e => ccGate(g, { account_id: s.account.id }, e.currentTarget)}>
          <span class="mono">${gcode(s.account.code)}</span> ${s.account.name}</button>`)}
        <button class="alloc-pill-sug alloc-pill-more" data-float-anchor ?disabled=${busy}
          @click=${e => openMorePop(g, e.currentTarget)}>${t('dashboard.more', 'More…')}</button>
        ${busy ? html`<span class="dash-tri-busy">${t('dashboard.allocating', 'Allocating…')}</span>` : []}
      </div>
    </div>`;
  };

  const triageView = () => {
    if(loading.value || err.value) return '';
    const d = data.value; if(!d) return '';
    const na = (d.needs_attention || {}).to_allocate || 0;
    const tri = triage.value, dm = doneMap.value, busyKey = triBusy.value, gone = allDone.value;
    void dm; void busyKey;             // read here so pill/done state re-renders this block
    if(gone) return html`<div class="dash-alldone">🎉 ${t('dashboard.all_done', 'All done! Every bank entry is allocated.')}</div>`;
    if(tri && tri.groups && tri.groups.length) return collCard('triage',
      html`${t('dashboard.allocate_bank', 'Allocate bank entries')} <span class="dash-tri-count">${tri.total}</span>`,
      t('dashboard.allocate_bank_hint', 'money that has moved but has no GL home yet'), '',
      html`${triErr.value ? html`<div class="row-err">${triErr.value}</div>` : ''}
      <div class="dash-tri-list">${tri.groups.map(g => groupCard(g, tri))}</div>
      <button class="btn" @click=${() => go(co('/bank'))}>${t('dashboard.open_bank', 'Open Bank')} →</button>`,
      'dash-tri');
    if(na > 0) return html`<div class="dash-attention" @click=${() => go(co('/bank'))}>
      <span class="dash-att-badge">${na}</span>
      <span>${na === 1 ? t('dashboard.one_alloc', '1 bank entry needs allocating') : na + ' ' + t('dashboard.n_alloc', 'bank entries need allocating')}</span>
      <span class="dash-att-go">${t('dashboard.go_to_bank', 'Go to Bank Accounts')} ${icon('chevron-right', {size: 14})}</span></div>`;
    return '';
  };

  // ── Drafts to send or confirm (§5) — replaces the old client-activity feed. Draft documents (not
  // issued yet → need confirming) plus issued-but-unsent ones. Compact clickable list; each row
  // opens the document editor (KEY_FOR_TYPE route + /edit, mirroring documents.js). A needs-action
  // panel: it does not render at all while loading, on error, or when there is nothing waiting.
  const draftsView = () => {
    const rowsD = draftsData.value;
    if(draftsErr.value) return '';
    if(rowsD == null || !rowsD.length) return '';
    return collCard('drafts', t('dashboard.drafts_title', 'Drafts to send or confirm'),
      t('dashboard.drafts_hint', 'draft documents to confirm, and issued ones not yet sent'),
      String(rowsD.length), html`
      <div class="tbl-wrap"><table class="list drafts-list">
        <thead><tr><th>${t('doc.col_number', 'Number')}</th><th>${t('common.date', 'Date')}</th>
          <th>${t('common.contact', 'Contact')}</th><th>${t('common.status', 'Status')}</th>
          <th class="ta-r">${t('doc.col_total', 'Total')}</th></tr></thead>
        <tbody>${rowsD.map(d => html`<tr class="clickable" title=${statusHelp(d.status)} @click=${() => openDraft(d)}>
          <td><span class="mono">${(isDraftDocNo(d.document_no) || !d.document_no) ? t('status.draft', 'Draft') : d.document_no}</span>
            <small class="doc-type-sub">${documentTypeLabel(d)}</small></td>
          <td class="mono">${formatDate(d.document_date, df())}</td>
          <td>${d.contact_name || ''}</td>
          <td><span class=${statusClass(d.status)}>${statusLabel(d.status)}</span></td>
          <td class="mono ta-r num">${fmtIn(d.total, docCurrency(d, draftsCur && draftsCur.byId, (draftsCur && draftsCur.base) || baseCurrency()))}</td></tr>`)}</tbody>
      </table></div>`);
  };

  // ── Batches to release (§4) — recurring batches due to run. Each row opens the batch
  // (/co/{id}/batches/{id}); shows its next run date, member count, and an Auto vs Manual mode
  // badge. Needs-action panel: hidden while loading / on error / when empty.
  const batchDate = iso => iso ? formatDate(iso, df()) : t('dashboard.batch_no_date', 'Not scheduled');
  const batchesView = () => {
    const rowsB = batchesData.value;
    if(rowsB == null || !rowsB.length) return '';
    return collCard('batches_due', t('dashboard.batches_title', 'Batches to release'),
      t('dashboard.batches_hint', 'recurring batches due to run — open one to review and release it'),
      String(rowsB.length), html`
      <div class="tbl-wrap"><table class="list batches-due-list">
        <thead><tr><th>${t('common.code', 'Code')}</th><th>${t('common.name', 'Name')}</th>
          <th>${t('dashboard.batch_next_run', 'Next run')}</th><th class="ta-r">${t('dashboard.batch_members', 'Members')}</th>
          <th>${t('dashboard.batch_mode', 'Mode')}</th></tr></thead>
        <tbody>${rowsB.map(b => html`<tr class="clickable" title=${t('dashboard.batch_open', 'Open this batch')} @click=${() => go(co('/batches/' + b.id))}>
          <td class="mono">${b.code || ''}</td>
          <td>${b.name || ''}</td>
          <td class="mono">${batchDate(b.next_run_date)}</td>
          <td class="mono ta-r">${b.member_count != null ? b.member_count : ''}</td>
          <td>${b.auto_run ? html`<span class="pill pill-open">${t('dashboard.batch_auto', 'Auto')}</span>` : html`<span class="pill pill-draft">${t('dashboard.batch_manual', 'Manual')}</span>`}</td></tr>`)}</tbody>
      </table></div>`);
  };

  // ── Bank Accounts (§5) — FULL WIDTH (own section, not the old 2-col dash-grid): every row opens
  // that account, balances plus a Total Cash row.
  const bankAccountsView = () => {
    if(loading.value || err.value) return '';
    const d = data.value; if(!d) return '';
    return collCard('bank_accounts', t('dashboard.bank_accounts', 'Bank Accounts'), t('dashboard.bank_hint', 'balances by account — tap one to open it'), '', html`
      <div class="tbl-wrap dash-bank-table"><table class="list">
        <thead><tr><th>${t('common.gl', 'GL')}</th><th>${t('common.account', 'Account')}</th><th class="ta-r">${t('common.balance', 'Balance')}</th></tr></thead>
        <tbody>${(d.bank_accounts || []).length ? (d.bank_accounts || []).map(b => {
          // each account in ITS OWN currency (own_balance when the API sends it); Total Cash below is
          // the base total, so it keeps the base mark (owner 2026-10-19: no currency bleeds)
          const f = bankFigures(b, b, { code: (d.company || {}).currency_code || baseCurrencyCode() });
          return html`<tr
          class="clickable" title=${t('dashboard.open_bank_account', 'Open this bank account')}
          @click=${() => go(co('/bank/' + b.id))}>
          <td class="mono" data-label=${t('common.gl', 'GL')}>${gcode(b.gl_code)}</td>
          <td data-label=${t('common.account', 'Account')}>${b.name}${b.account_no ? html` <span class="muted">(${b.account_no})</span>` : ''}</td>
          <td class="mono ta-r num" data-label=${t('common.balance', 'Balance')}>${numIn(f.balance, f.symbol)}</td></tr>`; })
          : html`<tr><td colspan="3" class="muted">${t('dashboard.no_banks', 'No bank accounts yet.')}</td></tr>`}
        <tr class="rp-total"><td></td><td data-label=${t('common.account', 'Account')}><b>${t('dashboard.total_cash', 'Total Cash')}</b></td>
          <td data-label=${t('common.balance', 'Balance')} class=${'mono ta-r num ' + totalTone(d.total_cash)}><b>${fmtRS(d.total_cash)}</b></td></tr></tbody>
      </table></div>
      <button class="btn" @click=${() => go(co('/bank'))}>${t('dashboard.open_bank', 'Open Bank')} →</button>`);
  };

  // A "this year vs last year" delta chip. goodWhenUp marks which direction is favourable (income /
  // net up = good; expenses up = bad) so the colour reads correctly. Empty when there is no prior.
  const deltaChip = (cur, prior, goodWhenUp) => {
    if(prior == null) return '';
    const c = Number(cur) || 0, p = Number(prior) || 0, diff = c - p;
    const pct = p !== 0 ? Math.round(diff / Math.abs(p) * 100) : null;
    const dir = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
    const good = dir === 'flat' ? null : ((dir === 'up') === goodWhenUp);
    const arrow = dir === 'up' ? '↑' : dir === 'down' ? '↓' : '→';
    const txt = pct == null ? fmtR(diff) : (diff > 0 ? '+' : '') + pct + '%';
    return html`<span class=${'dash-delta ' + (good == null ? 'flat' : good ? 'good' : 'bad')}
      title=${t('dashboard.vs_last_year', 'vs last year') + ': ' + fmtR(p)}>${arrow} ${txt}</span>`;
  };

  // One pair of bars per fiscal month: current FY beside the matching prior FY. The chart gets its
  // data from the same posted-income ledger movements as P&L, not invoice totals, so credit notes
  // and every other posted income source are reflected correctly.
  function turnoverChart(d){
    const series = ((d.pnl || {}).turnover_months) || {};
    const months = series.months || [];
    const max = Math.max(1, ...months.flatMap(m => [Math.abs(Number(m.current) || 0), Math.abs(Number(m.prior) || 0)]));
    const wrap = document.createElement('div');
    wrap.className = 'dash-fy-chart';
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', t('dashboard.turnover_by_fy', 'Monthly turnover: financial year {current} compared with {prior}')
      .replace('{current}', series.fy_year || '').replace('{prior}', series.prior_fy_year || ''));
    const legend = document.createElement('div'); legend.className = 'dash-fy-legend';
    [["current", series.fy_year], ["prior", series.prior_fy_year]].forEach(([kind, year]) => {
      const item = document.createElement('span'); item.className = 'dash-fy-key ' + kind;
      item.textContent = String(year || ''); legend.append(item);
    });
    const cols = document.createElement('div'); cols.className = 'dash-fy-cols';
    months.forEach(month => {
      const col = document.createElement('div'); col.className = 'dash-fy-col';
      const bars = document.createElement('div'); bars.className = 'dash-fy-bars';
      [['prior', month.prior], ['current', month.current]].forEach(([kind, raw]) => {
        const value = Number(raw) || 0;
        const bar = document.createElement('span'); bar.className = 'dash-fy-bar ' + kind + (value < 0 ? ' neg' : '');
        bar.dataset.h = Math.max(value ? 3 : 0, Math.abs(value) / max * 100).toFixed(1) + '%';
        bar.title = month.month + ' ' + (kind === 'current' ? series.fy_year : series.prior_fy_year) + ': ' + fmtR(value);
        bars.append(bar);
      });
      const label = document.createElement('span'); label.className = 'dash-fy-label'; label.textContent = month.month || '';
      col.append(bars, label); cols.append(col);
    });
    wrap.append(legend, cols);
    wrap.querySelectorAll('[data-h]').forEach(el => { el.style.height = el.dataset.h; });
    return wrap;
  }

  // ── Profit & Loss (§6) — FULL WIDTH, now comparing this year vs last year (pnl.month_prior /
  // ytd_prior) with a delta per row, plus the financial-year turnover comparison.
  const pnlView = () => {
    if(loading.value || err.value) return '';
    const d = data.value; if(!d || !d.pnl) return '';
    const windows = [
      [d.pnl.month_label || t('dashboard.this_month', 'This month'), d.pnl.month, d.pnl.month_prior],
      [t('common.year_to_date', 'Year to date'), d.pnl.ytd, d.pnl.ytd_prior],
    ];
    return collCard('pnl', t('dashboard.profit_loss', 'Profit & Loss'), t('dashboard.pnl_hint', 'this year vs last year — this month and the year to date'), '', html`
      <div class="dash-pnl">${windows.map(([title, blk, prior]) => html`<div class="dash-pnl-col">
        <div class="dash-pnl-title">${title}</div>
        <div class="dash-pnl-row"><span>${t('common.income', 'Income')}</span><span class="dash-pnl-fig"><span class="mono">${fmtR((blk || {}).income)}</span>${deltaChip((blk || {}).income, (prior || {}).income, true)}</span></div>
        <div class="dash-pnl-row"><span>${t('common.expenses', 'Expenses')}</span><span class="dash-pnl-fig"><span class="mono">${fmtR((blk || {}).expenses)}</span>${deltaChip((blk || {}).expenses, (prior || {}).expenses, false)}</span></div>
        <div class="dash-pnl-row dash-pnl-net"><span>${((blk || {}).net || 0) >= 0 ? t('dashboard.net_profit', 'Net Profit') : t('dashboard.net_loss', 'Net Loss')}</span><span class="dash-pnl-fig"><span class=${'mono ' + totalTone((blk || {}).net)}>${num((blk || {}).net)}</span>${deltaChip((blk || {}).net, (prior || {}).net, true)}</span></div>
      </div>`)}</div>
      <div class="dash-bars-title">${t('dashboard.turnover_by_fy', 'Turnover by financial year')}</div>
      ${turnoverChart(d)}
      <button class="btn" @click=${() => go(co('/reports/income-statement'))}>${t('dashboard.full_reports', 'Full Reports')} →</button>`);
  };

  // ── Recent Activity (§7, LAST) — FULL WIDTH: latest posted movements; each row opens its source.
  const recentActivityView = () => {
    if(loading.value || err.value) return '';
    const d = data.value; if(!d) return '';
    const dmy = iso => formatDate(iso, df());
    return collCard('recent_activity', t('dashboard.recent_activity', 'Recent Activity'), t('dashboard.activity_hint', 'the latest posted movements — tap a row to open it'), '', html`
      <div class="tbl-wrap dash-activity-table"><table class="list">
        <thead><tr><th>${t('common.date', 'Date')}</th><th>${t('common.description', 'Description')}</th><th>${t('common.gl', 'GL')}</th><th>${t('common.source', 'Source')}</th><th class="ta-r">${t('common.amount', 'Amount')}</th></tr></thead>
        <tbody>${(d.recent_activity || []).length ? (d.recent_activity || []).map(r => html`<tr
          class="clickable" title=${activityTitle(r)}
          @click=${() => go(activityDest(r))}>
          <td class="mono" data-label=${t('common.date', 'Date')}>${dmy(r.date)}</td><td data-label=${t('common.description', 'Description')}>${r.description || (r.reference || '')}</td>
          <td class="mono" data-label=${t('common.gl', 'GL')}>${gcode(r.gl_code)}</td><td data-label=${t('common.source', 'Source')}>${(r.source || '').replace('_', ' ')}</td>
          <td class="mono ta-r num" data-label=${t('common.amount', 'Amount')}>${num(r.debit ? r.debit : -r.credit)}</td></tr>`)
          : html`<tr><td colspan="5" class="muted">${t('dashboard.no_activity', 'No recent activity.')}</td></tr>`}</tbody>
      </table></div>
      <button class="btn" @click=${() => go(co('/reports/transactions'))}>${t('common.all', 'All')} ${t('dashboard.transactions', 'transactions')} →</button>`);
  };

  // ── Bank inbox (seamless bank flow) — the cross-account unallocated-lines worklist. Stable node
  // (self-fetching, its own reactive body); it hides itself entirely when nothing needs allocating.
  const bankInboxNode = bankInboxSection(companyId, { go });

  // Redesigned dashboard order (owner 2026-09-19): head · 1 KPIs · 2 bank inbox · 3 drafts ·
  // 4 batches · 5 bank accounts · 6 P&L (vs last year) · 7 recent activity. The three needs-action
  // panels (inbox / drafts / batches) render only when they have something. Top-level ${()=>…}
  // slots in this once-built template are safe; the bank-inbox stable node never re-renders.
  return tenantShell(companyId, t('common.dashboard', 'Dashboard'), html`<div class="page page-wide dash-page">
    <h1 class="sr-only">${() => t('common.dashboard', 'Dashboard')}</h1>
    ${() => kpisView()}
    ${bankInboxNode}
    ${() => draftsView()}
    ${() => batchesView()}
    ${() => bankAccountsView()}
    ${() => pnlView()}
    ${() => recentActivityView()}
  </div>`);
}
